import axios from "axios";
import { getApiKey } from "./storage.ts";
import { cleanData } from "./utils.ts";

const STIXIFY_API_BASE_URL = process.env.REACT_APP_API_BASE_URL + '/stixify_api';

type PaginatedResponse<T> = {
  count: number;
  results: T[];
};

export type Profile = {
  id: string;
  name: string;
  extractions: string[];
  relationship_mode: string;
  ai_create_attack_flow: boolean;
  extract_text_from_image: boolean;
  stixify_profile_metadata: {
    id: string;
    created: string;
    name: string;
    extractions: string[];
    whitelists: string[];
    aliases: string[];
    relationship_mode: string;
    extract_text_from_image: boolean;
    ignore_extraction_boundary: boolean;
    ignore_image_refs: boolean;
    defang: boolean;
    ignore_link_refs: boolean;
    ai_summary_provider: boolean;

    ai_settings_extractions: string[];
    ai_settings_relationships: string;
  }
}


const apiRequest = async <T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  data?: any,
  headers: Record<string, string> = {},
  params: Record<string, string | number | undefined> = {},
) => {
  try {
    const response = await axios<T>({
      method,
      url: `${STIXIFY_API_BASE_URL}${path}`,
      data,
      headers: {
        Authorization: `Token ${getApiKey()}`,
        ...headers,
      },
      params,
    });
    return response;
  } catch (error) {
    if (error?.response?.status === 401) {
      window.location.href = '/logout-redirect'
    }
    throw error;
  }
};

export const fetchStixifyProfiles = (page_number: number) => {
  return apiRequest<PaginatedResponse<Profile>>('GET', `/staff-profiles/?page=${page_number}`);
};

export const fetchStixifyProfile = (id: string) => {
  return apiRequest<Profile>('GET', `/proxy/profiles/${id}/`);
};

export const deleteStixifyProfile = (id: string) => {
  return apiRequest<any>('DELETE', `/proxy/profiles/${id}/`);
};

export const loadAliases = (team_id: string, page: number) => {
  return apiRequest<any>('GET', `/proxy/aliases/?page_size=100`, { page }).then(res => res.data.aliases);
};

export const loadExtractors = (page: number) => {
  return apiRequest<any>(
    'GET',
    `/extractors/`,
    {}, {},
    { page, page_size: 50 }
  ).then(res => res.data.extractors);
};

export const loadAllExtractions = async () => {
  let hasNext = true
  let page = 1
  let alliases: any[] = []
  while (hasNext) {
    const res = await loadExtractors(page)
    alliases = [...alliases, ...res]
    page += 1
    hasNext = res.length > 0
  }
  return alliases
};

export const loadWhitelists = () => {
  return apiRequest<any>('GET', `/proxy/whitelists/?page_size=100`).then(res => res.data.whitelists);
};

export const editProfile = async (id: string, data: any) => {
  return apiRequest<any>('PATCH', `/proxy/profiles/${id}/`, data);
};

export const createProfile = async (data: any) => {
  return apiRequest<any>('POST', '/profiles/', data);
};

export const changeDefaultStixifyProfile = async (profile_id: string) => {
  return apiRequest('POST', `/profiles/${profile_id}/set-default/`)
};

export const createReport = async (team_id: string, file: File, data: any) => {
  const formData = new FormData();
  formData.append('file', file);
  Object.keys(data).forEach(key => {
    if (key == 'labels') {
      if (data[key].length === 0) {
        return
      }
    }
    if (data[key]) {
      formData.append(key, data[key])
    }
  })
  return apiRequest<Report>('POST', `/teams/${team_id}/reports/`, formData);
};



export const deleteDossier = async (team_id: string, id: string) => {
  return apiRequest<any>('DELETE', `/teams/${team_id}/dossiers/${id}/`);
};

export const updateDossier = async (team_id: string, id: string, data: any) => {
  return apiRequest<any>('PUT', `/teams/${team_id}/dossiers/${id}/`, data);
};

export const createDossier = async (team_id: string, data: any) => {
  return apiRequest<any>('POST', `/teams/${team_id}/dossiers/`, cleanData(data));
};

export type Dossier = {
  name: string;
  description: string;
  tlp_level: string;
  labels: string[];
  created_at: string;
  updated_at: string;
  report_count: number;
  id: string;
  team: {
    id: string;
    name: string;
  }
}


export const removeReportFromDossier = (team_id: string, dossier_ids: string[], report_id: string) => {
  return apiRequest<Dossier>('POST', `/teams/${team_id}/dossiers/remove-report/`, { dossier_ids, report_id });
};

export const addReportToDossiers = (team_id: string, dossier_ids: string[], report_id: string) => {
  return apiRequest<Dossier>('POST', `/teams/${team_id}/dossiers/add-report/`, { dossier_ids, report_id });
};

export const fetchDossier = (team_id: string | undefined, dossier_id: string) => {
  const team_path = team_id ? `/teams/${team_id}` : ''
  return apiRequest<Dossier>('GET', `${team_path}/dossiers/${dossier_id}/`);
};

export const fetchDossierReports = (team_id: string | undefined, dossier_id: string, filter: {}, page: number) => {
  const team_path = team_id ? `/teams/${team_id}` : ''
  return apiRequest<PaginatedResponse<Report>>('GET', `${team_path}/dossiers/${dossier_id}/reports/`, {}, {}, { ...filter, page });
};

export const fetchDossiers = (team_id: string | undefined, filter: {}, page: number, sortField: string, sortOrder: string) => {
  const path = team_id ? `/teams/${team_id}/dossiers/` : '/dossiers/'
  return apiRequest<PaginatedResponse<Dossier>>('GET', path, {}, {}, {
    ...filter,
    page,
    ordering: (sortOrder === 'asc' ? '' : '-') + sortField,
  });
};

export const fetchReportDossiers = (team_id: string | undefined, report_id: string) => {
  const path = team_id ? `/teams/${team_id}/reports/${report_id}/dossiers/` : `/reports/${report_id}/dossiers/`
  return apiRequest<PaginatedResponse<Dossier>>('GET', path);
}

export const createTeamProfile = async (team_id: string, data: any) => {
  return apiRequest<any>('POST', `/teams/${team_id}/profiles/`, data);
};

export const updateProfileExtraction = (profileId, data) => {
  return apiRequest<PaginatedResponse<TeamProfile>>('PATCH', `/staff-profiles/${profileId}/`, data);
}

export const fetchTeamStixifyProfile = (team_id: string, id: string) => {
  return apiRequest<Profile>('GET', `/teams/${team_id}/profiles/${id}/`);
};

export const userFetchTeamStixifyProfileDetail = (profile_id: string) => {
  return apiRequest<Profile>('GET', `/staff-profiles/${profile_id}/`);
}

export const deleteTeamStixifyProfile = (team_id: string, id: string) => {
  return apiRequest<any>('DELETE', `/teams/${team_id}/profiles/${id}/`);
};

export const changeDefaultTeamStixifyProfile = async (team_id: string, profile_id: string) => {
  return apiRequest('POST', `/teams/${team_id}/profiles/${profile_id}/set-default/`)
};


type FileIdentity = {
  type: "identity";
  spec_version: string;
  id: string;
  created_by_ref: string;
  created: string; // Use Date if you want to parse it as a Date object
  modified: string; // Use Date if you want to parse it as a Date object
  name: string;
  description: string;
  identity_class: string;
  sectors: string[];
  contact_information: string;
  object_marking_refs: string[];
};

type FileData = {
  id: string;
  report_id: string;
  mimetype: string;
  profile_id: string;
  mode: string; // e.g., "txt"
  download_url: string;
  ai_summary_provider: string;
  name: string;
  tlp_level: string; // e.g., "green"
  confidence: number; // Assuming this is a percentage
  labels: string[];
  identity: FileIdentity;
  created: string; // Use Date if you want to parse it as a Date object
  modified: string; // Use Date if you want to parse it as a Date object
};

export type Report = {
  id: string;
  created_at: string;
  stixify_file_metadata: FileData,
  state: string,
  last_run_time: string,
  team_name: string,
  team_id: string,
  dossier_count: number,
  profile_id: string;
  profile_name?: string;
  stixify_job_metadata: {
    id: string
  }
}

export const fetchReports = (team_id: string | undefined, filter: any, sortField: string, sortOrder: 'asc' | 'desc', page_number: number) => {
  if (team_id) {
    return apiRequest<PaginatedResponse<Report>>('GET', `/teams/${team_id}/reports/`, {}, {}, {
      page: page_number,
      ...filter,
      ordering: (sortOrder === 'asc' ? '' : '-') + sortField,
    });
  } else {
    return apiRequest<PaginatedResponse<Report>>('GET', `/reports/`, {}, {}, {
      page: page_number,
      ...filter,
      ordering: (sortOrder === 'asc' ? '' : '-') + sortField,
    });
  }
};

export const fetchReport = (team_id: string | undefined, report_id: string) => {
  if(team_id) {
    return apiRequest<Report>('GET', `/teams/${team_id}/reports/${report_id}/`);
  } else {
    return apiRequest<Report>('GET', `/reports/${report_id}/`);
  }
};

export const fetchReportSummary = (team_id: string | undefined, report_id: string) => {
  if(!team_id) {
    return apiRequest<string>('GET', `/reports/${report_id}/summary/`);
  }
  return apiRequest<string>('GET', `/teams/${team_id}/reports/${report_id}/summary/`);
};

export const fetchReportFileDownloadUrl = (team_id: string | undefined, report_id: string) => {
  if(!team_id) {
    return apiRequest<{
      file_name: string;
      url: string;
    }>('GET', `/reports/${report_id}/download-report/`);
  }
  return apiRequest<{
    file_name: string;
    url: string;
  }>('GET', `/teams/${team_id}/reports/${report_id}/download-report/`);
};

export const fetchReportMarkdown = (team_id: string | undefined, report_id: string) => {
  if(!team_id) {
    return apiRequest<string>('GET', `/reports/${report_id}/markdown/`);
  }
  return apiRequest<string>('GET', `/teams/${team_id}/reports/${report_id}/markdown/`);
};


export const deleteReportFromObject = (team_id: string | undefined, object_id: string, report_id: string) => {
  if(!team_id) {
  return apiRequest('POST', `/reports/${report_id}/delete-object/`, {
    object_id
  }, {}, {})
  }
  return apiRequest('POST', `/teams/${team_id}/reports/${report_id}/delete-object/`, {
    object_id
  }, {}, {})
}

export interface ExtractionObject {
  type: string,
  id: string,
  value: string,
  name: string,
  additionalProp: Object,
  x_mitre_domains: string[],
  x_capec_version: string;
  attackType: string;
  description: string;
  external_references?: {
    external_id: string;
  }[];
  hashes?: [key: string, value: string];
  iban_number?: string;
  key?: string;
  number?: string;
  path?: string;
  string?: string;
  address?: string;
}

interface ExtractionObjectsResponse {
  page_size: number;
  page_number: number;
  page_results_count: number;
  total_results_count: number;
  objects: ExtractionObject[];
}

export interface IJob {
  id: string;
  report: string;
  run_datetime: string;
  state: string;
  metadata: {
    error: string;
    completion_time: string;
  }
}

interface ExtractionReportsResponse {
  page_size: number;
  page_number: number;
  page_results_count: number;
  total_results_count: number;
  reports: Report[];
}

export const fetchReportObjects = (team_id: string | undefined, report_id: string, page: number) => {
  if (!team_id) {
    return apiRequest<ExtractionObjectsResponse>('GET', `/reports/${report_id}/objects/`, {}, {}, {
      page,
      ignore_embedded_sro: 'true',
    });
  }
  return apiRequest<ExtractionObjectsResponse>('GET', `/teams/${team_id}/reports/${report_id}/objects/`, {}, {}, {
    page,
    ignore_embedded_sro: 'true',
  });
};

export const fetchJobs = (team_id: string | undefined, page: number, sortField: string, sortOrder: string) => {
  if(!team_id) {
    return apiRequest<PaginatedResponse<IJob>>('GET', `/jobs/`, {}, {}, {
      page,
      ordering: (sortOrder === 'asc' ? '' : '-') + sortField,
    });
  }
  return apiRequest<PaginatedResponse<IJob>>('GET', `/teams/${team_id}/jobs/`, {}, {}, {
    page,
    ordering: (sortOrder === 'asc' ? '' : '-') + sortField,
  });
}

export const fetchTeamJobDetail = (team_id: string | undefined, id: string) => {
  if(!team_id) return apiRequest<IJob>('GET', `/jobs/${id}/`, {}, {}, {});
  return apiRequest<IJob>('GET', `/teams/${team_id}/jobs/${id}/`, {}, {}, {});
}


export const scoSearch = (types: string, value: string, page: number) => {
  return apiRequest<ExtractionObjectsResponse>('GET', `/objects/scos/`, {}, {}, { types, value, page });
};


export const getObjectById = async (objectId: string) => {
  try {
    return await apiRequest<ExtractionObjectsResponse>('GET', `/objects/${objectId}/`, {}, {}, {});
  } catch(err) {
    return {
      data: {
        objects: [],
        page_number: 1,
        total_results_count: 0,
      }
    }
  }
}

export const sdoSearch = (types: string, value: string, page: number) => {
  return apiRequest<ExtractionObjectsResponse>('GET', `/objects/sdos/`, {}, {}, { types, value, page });
};

export const getReportsByExtraction = (team_id: string, object_id: string, page: number) => {
  return apiRequest<ExtractionReportsResponse>('GET', `/teams/${team_id}/reports/objects/${object_id}/`, {}, {}, { page });
}